import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../Core/redux/hooks";
import { Dropdown, Tooltip } from "antd";
import {
  updateConversations,
  updateConversationsMultiple,
} from "../../Core/redux/slices/conversations.slice";
import { downloadFiles } from "../../Core/utils";
import dayjs from "dayjs";
import CustomAvatar from "../../Core/CommonV2/CustomAvatar";
import UserDetailsCard from "./UserDetailsCard";
import ParseMentions from "./ParseMentions";
import { EmojiIcon, EmojiIconV3, EmojiWhiteIcon } from "../../Core/svgV2/EmojiIcon";
import { MessageReplyIcon, MessageReplyIconV3 } from "../../Core/svgV2/MessageReplyIcon";
import { DownloadIcon } from "../../Core/svgV2/DownloadIcon";
import { EyeOffIcon, EyeOpenIcon } from "../../Core/svgV2/EyeIcon";
import { EllipseIcon } from "../../Core/svgV2/Ellipse";
import { SaveIcon } from "../../Core/svgV2/SaveIcon";
import { ForwardIcon, ForwardV3Icon, ForwardV3MonoIcon } from "../../Core/svgV2/ForwardIcon";
import { EditIcon, EditV3Icon } from "../../Core/svgV2/EditIcon";
import { DeleteIcon, DeleteV3Icon } from "../../Core/svgV2/DeleteIcon";
import { ChevronIcon } from "../../Core/svgV2/Chevron";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import usePermission from "../../Core/hooks/usePermission";
import { REACTIONS } from "../../Core/constants";
import getImageWeServeUrl from "../../Core/utils/getImageWeServeUrl";
import CopyIcon from "../../Core/svgV3/CopyIcon";
import StarIcon from "../../Core/svgV3/StarIcon";
import { PinnedIcon, PinnedOutlinedIcon, UnpinIcon } from "../../Core/svgV2/Pinned";
import TickIcon, { DoubleTickIcon, SingleTickIcon } from "../../Core/svgV3/TickIcon";
import { updateDashboard } from "../../Core/redux/slices/dashboard.slice";
import AppleEmoji from "../../Core/Common/AppleEmoji";
import Picker, { EmojiStyle } from "emoji-picker-react";
import UnstarIcon from "../../Core/svgV3/UnstarIcon";
import PinDurationModal from "./Modals/PinDurationModal";
import { UnPinIcon } from "../../Core/svgV3/UnPinIcon";

export const Attachments = ({
  type,
  url,
  thumbnail,
  showName = false,
  className = "w-[329px] h-[178px]",
  fileName = "",
  onClick,
}) => {
  if (type.includes("video")) {
    return (
      <div className="px-2 py-2 overflow-hidden rounded">
        <video muted autoPlay={false} controls preload="metadata">
          <source src={`${url}#t=0.1`}></source>
        </video>
      </div>
    );
  }

  const [showThumnail, setShowThumnail] = useState(true);

  return (
    <>
      <div
        className={`mx-2 my-2 border border-black cursor-pointer rounded overflow-hidden ${className} flex justify-center items-center object-cover relative actions-row bg-white`}
        onClick={onClick}
      >
        {showThumnail && (
          <img
            className={`${thumbnail ? "w-full h-full" : "h-24 w-24"} rounded object-cover`}
            src={thumbnail || "/images/No-Image-Placeholder.svg"}
          />
        )}
        <img
          className={`${url ? "w-full h-full" : "h-24 w-24"} rounded object-cover ${showThumnail ? "hidden" : ""}`}
          src={url || "/images/No-Image-Placeholder.svg"}
          onLoad={() => setShowThumnail(false)}
        />
      </div>
      {showName && (
        <div className="flex mx-2 my-2">
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 20 20">
              <rect width="15" height="15" x="2.5" y="2.5" fill="#E0E7EE" rx="4"></rect>
              <path stroke="#0D3271" strokeLinecap="round" strokeLinejoin="round" d="M10.833 5.946v1.721c0 .234 0 .35.046.44.04.078.104.142.182.182.089.045.206.045.44.045h1.72m-2.388 3.75h-2.5m3.334-1.667H8.333m5-1.255v3.005c0 .7 0 1.05-.136 1.318a1.25 1.25 0 01-.546.546c-.268.136-.617.136-1.318.136H8.667c-.7 0-1.05 0-1.318-.136a1.25 1.25 0 01-.546-.546c-.136-.268-.136-.618-.136-1.318V7.834c0-.7 0-1.05.136-1.317a1.25 1.25 0 01.546-.547c.268-.136.618-.136 1.318-.136h1.338c.306 0 .459 0 .602.035.128.03.25.08.362.15.126.076.234.185.45.4l1.329 1.33c.216.215.324.323.401.45.069.111.12.233.15.36.034.145.034.298.034.603z"></path>
            </svg>
          </div>
          <p className="mon-500-12 truncate w-[315px] pl-2">{fileName}</p>
        </div>
      )}
    </>
  );
};

export const Thumbnail = ({
  type = "",
  url,
  className = "w-[345px] h-[174px]",
  file,
}) => {
  const [isHover, setIsHover] = useState(false);

  if (type.includes("video")) {
    return (
      <div className={`${className} flex flex-col bg-white rounded-[12px]`}>
        <div className="w-[329px] h-[130px] rounded-[8px] my-[8px] mx-[8px] overflow-hidden">
          <video className="w-[329px] h-[130px]" muted autoPlay={false} controls preload="metadata">
            <source src={`${url}#t=0.1`}></source>
          </video>
        </div>
        <div
          className="flex flex-row mb-[8px] mx-[8px] justify-between"
          onMouseEnter={() => setIsHover(true)}
          onMouseLeave={() => setIsHover(false)}
        >
          <Tooltip title={file?.name} placement="bottomLeft">
            <div className="flex text-gray-700 gap-x-[8px] out-500-12 items-end h-[20px] min-w-[155px] max-w-[180px]">
              <img src={"/images/v2/conversations/Video.svg"} />
              <div className="truncate max-w-[120px]">{file?.name}</div>
            </div>
          </Tooltip>
          <div>{isHover ? <DownloadIcon className="out-500-12" /> : ""}</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${className} flex flex-col bg-white rounded-[12px] border border-gray-100`}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <div className="w-[329px] h-[130px] rounded-[8px] my-[8px] mx-[8px] overflow-hidden">
        <img src={"/images/icons/no-files.svg"} className="object-contain w-full h-full" />
      </div>
      <div className="flex flex-row mb-[8px] mx-[8px] justify-between items-center">
        <div className="flex flex-row text-gray-700 gap-x-[8px] out-500-12 items-end h-[20px] w-[155px]">
          <img src={"/images/v2/conversations/pdf.svg"} />
          <p className="truncate whitespace-nowrap">{file.name}</p>
        </div>
        <div className="flex items-center justify-center w-5 h-5">
          {isHover ? (
            <button onClick={() => { downloadFiles([url], file.fileSize); }}>
              <DownloadIcon className="out-500-12" />
            </button>
          ) : ""}
        </div>
      </div>
    </div>
  );
};

const ImageComponent = ({ src, isFinal = false, message, file, currentIndex }) => {
  const [hover, setHover] = useState(false);
  const dispatch = useDispatch();

  if (isFinal) {
    return (
      <div
        className="h-[100px] w-[147px] object-cover rounded-lg border border-gray-200 flex items-center justify-center"
        style={{
          background: `linear-gradient(0deg, rgba(0,0,0,0.2), rgba(0,0,0,0.2)), url(${src})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
        }}
      >
        <span className="text-white out-500-16">+2</span>
      </div>
    );
  }

  const items = [
    { key: "1", label: <div onClick={() => dispatch(updateConversations({ key: "filePreview", value: { visible: true, currentIndex, from: "messages", docs: message.attachments, createdAt: message.createdAt } }))} className="text-gray-700 out-300-14">Preview</div>, icon: <EyeOpenIcon className="text-gray-700" /> },
    { key: "2", label: <div onClick={() => dispatch(updateConversations({ key: "sharedFileDetailsDrawer", value: { visible: true, data: { ...file, senderName: message.senderName, createdAt: message.createdAt } } }))} className="text-gray-700 out-300-14">View details</div>, icon: <EllipseIcon className="text-gray-700" /> },
    { key: "3", label: <div onClick={() => downloadFiles([src], file.fileSize)} className="text-gray-700 out-300-14">Download</div>, icon: <DownloadIcon className="text-gray-700" /> },
    !file.savedInVault ? { key: "4", type: "divider" } : null,
    !file.savedInVault ? { key: "5", label: <div onClick={() => dispatch(updateConversations({ key: "saveInLibrary", value: { visible: true, file } }))} className="text-gray-700 out-300-14">Save in library</div>, icon: <SaveIcon className="text-gray-700" /> } : null,
  ];

  return (
    <div className="relative" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div className="max-h-[102px] max-w-[147px] min-h-[102px] min-w-[147px] rounded-lg border flex justify-center items-center border-gray-200">
        <img
          className={`max-h-[100px] max-w-[147px] cursor-pointer ${file?.imageOutOfBound ? "" : "object-cover"}`}
          src={file?.imageOutOfBound ? src : getImageWeServeUrl(src, { w: "147", h: "102", file })}
          onClick={() => dispatch(updateConversations({ key: "filePreview", value: { visible: true, currentIndex, from: "messages", docs: message.attachments, createdAt: message.createdAt } }))}
        />
      </div>
      <div className={`bg-white ${hover ? "flex" : "hidden"} absolute top-1 right-1 h-9 border border-gray-100 justify-center items-center rounded-[4px]`}>
        <Tooltip title="Download" getPopupContainer={(t) => t.parentElement}>
          <button className="w-5 h-5 mx-2" onClick={() => downloadFiles([src], file.fileSize)}><DownloadIcon height={20} width={20} /></button>
        </Tooltip>
        <Tooltip title="Forward" getPopupContainer={(t) => t.parentElement}>
          <button className="w-5 h-5 mx-2" onClick={() => dispatch(updateConversationsMultiple([{ key: "shareMessageModal", value: true }, { key: "forwardMessage", value: { ...message, attachments: message.attachments.filter((a) => a.url === src) } }, { key: "shareType", value: "forwardMessage" }]))}>
            <MessageReplyIcon className="text-gray-700 scale-x-[-1]" />
          </button>
        </Tooltip>
        <Tooltip title="More Options" getPopupContainer={(t) => t.parentElement}>
          <button className="w-5 h-5 mx-2">
            <Dropdown menu={{ items }} trigger={["click"]} dropdownRender={(menu) => <div className="w-[200px]">{menu}</div>}>
              <ChevronIcon className="text-gray-700" />
            </Dropdown>
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

const Album = ({ files, message }) => {
  const images = files.filter((f) => f.type?.includes("image")) || [];
  const otherFiles = files.filter((f) => !f.type?.includes("image")) || [];
  return (
    <div className="pb-1">
      {images.length > 0 && (
        <div className="flex mt-1 gap-x-3">
          {images.slice(0, 5).map((img, i) => (
            <ImageComponent key={i} src={img.url} isFinal={i === 4 && images.length > 5} message={message} file={img} currentIndex={i} />
          ))}
        </div>
      )}
      {otherFiles.length > 0 && (
        <div className="flex flex-col mt-1 gap-y-3">
          {otherFiles.slice(0, 5).map((f, i) => (
            <div key={i}><Thumbnail type={f.type} url={f.url} file={f} /></div>
          ))}
        </div>
      )}
    </div>
  );
};

// Three vertical dots icon
const DotsVerticalIcon = ({ className = "" }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <circle cx="8" cy="3.5" r="1.5" fill="currentColor" />
    <circle cx="8" cy="8" r="1.5" fill="currentColor" />
    <circle cx="8" cy="12.5" r="1.5" fill="currentColor" />
  </svg>
);

const InfoIcon = ({ className = "" }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2" />
    <path d="M8 7.2v4M8 5.2h.01" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const RequestDocIcon = ({ className = "" }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
    <rect x="3.5" y="2" width="9" height="12" rx="1.2" stroke="currentColor" strokeWidth="1.1" />
    <path d="M5.8 6h4.4M5.8 8.4h4.4M5.8 10.8h2.6" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
  </svg>
);

// Maps an attachment mime type to the icon + label shown in a reply quote,
// matching the same file-type tag used in the compose-time reply preview
// (ReplyMessageFlow.tsx) so both surfaces stay visually consistent.
const getReplyFileTag = (mimeType = "") => {
  if (mimeType.includes("pdf")) {
    return { url: "/images/v2/library/pdf-file-icon.svg", type: "Pdf" };
  } else if (mimeType.includes("excel") || mimeType.includes("spreadsheet")) {
    return { url: "/images/v2/library/excel-file-icon.svg", type: "Excel" };
  } else if (mimeType.includes("image")) {
    return { url: "/images/v2/library/image-file-icon.svg", type: "Photo" };
  } else if (mimeType.includes("video")) {
    return { url: "/images/v2/library/video-file-icon.svg", type: "Video" };
  } else {
    return { url: "/images/v2/library/default-file-icon.svg", type: "File" };
  }
};

// ── Activity type constants ─────────────────────────────────────────────
// Mirrors mobile's Core/utils/ActivityConstants (React Native app).
// GROUP_CREATED_ACTIVITY = 3 is confirmed by the backend controller
// (addGroupController hardcodes activityType: 3). Please verify the other
// three against the actual ActivityConstants file/module — these values
// are placeholders copied positionally from mobile's switch order.
const REQUEST_ACKNOWLEDGED = 1;
const GROUP_MEMBER_ADDED = 2;
const GROUP_CREATED_ACTIVITY = 3;
const GROUP_MEMBER_REMOVE = 4;

const Activity = ({ activity, message, user, threadInfo }) => {
  const senderId = message.senderId ?? message.sender;
  const name = +user.id === +senderId ? "You" : message.senderName;

  const pillClass =
    "px-[10px] py-1 mx-auto mt-0 mb-[10px] border border-[#DDE5ED] w-max max-w-[80%] rounded-[12px] bg-[#F9FBFE]";
  const textClass = "text-[#344054] text-xs leading-[18px] font-medium text-center";

  switch (activity.activityType) {
    case REQUEST_ACKNOWLEDGED:
      return (
        <div className={pillClass}>
          <p className={textClass}>
            {name} acknowledged this request on{" "}
            {dayjs(activity.activityTime).format("MMM DD, YYYY [at] hh:mm A")}
          </p>
        </div>
      );
    case GROUP_CREATED_ACTIVITY:
      return (
        <div className={pillClass}>
          <p className={textClass}>
            {name} created group "
            {activity.groupName || threadInfo?.groupName || threadInfo?.name}"
          </p>
        </div>
      );
    case GROUP_MEMBER_ADDED:
      return (
        <div className={pillClass}>
          <p className={textClass}>
            {name} added {activity.userName}
          </p>
        </div>
      );
    case GROUP_MEMBER_REMOVE:
      return (
        <div className={pillClass}>
          <p className={textClass}>
            {name} removed {activity.userName}
          </p>
        </div>
      );
    default:
      console.warn("[Activity] No case matched for activityType:", activity?.activityType);
      return null;
  }
};

const Message = ({
  message,
  reactToMessage,
  onStarMessage,
  deleteMessage,
  comments,
  allowHover = true,
  showSender = true,
  handleMarkAsUnread,
  participantsWithoutUser = [],
  isDM = false,
  onPinMessage,
  onUnpinMessage,
}: {
  message: any;
  comments?: boolean;
  allowHover?: boolean;
  reactToMessage?: (emoji: string, messageId: string, isAlreadyReacted: boolean) => void;
  onStarMessage?: (message: any, isStarred: boolean) => void;
  handleMarkAsUnread?: (message: object) => void;
  deleteMessage?: (id: string) => void;
  showSender?: boolean;
  participantsWithoutUser?: [],
  isDM?: boolean;
  onPinMessage?: (message: any, duration: string) => void;
  onUnpinMessage?: (message: any) => void;
}) => {
  const { tenantMembers } = useAppSelector((state) => state.tenant);
  const { user } = useAppSelector((state) => state.userDetails);
  const currentThread = useAppSelector((state) => state.conversations.currentThread);
  const pinnedMessages = useAppSelector((state) => state.conversations.pinnedMessages);
  const [pinModalVisible, setPinModalVisible] = useState(false);
  const [pinTargetMessage, setPinTargetMessage] = useState<any>(null);
  const dispatch = useAppDispatch();
  const [repliesOpen, setRepliesOpen] = useState(false);
  const [openReactionFor, setOpenReactionFor] = useState(null);
  const [showFullPicker, setShowFullPicker] = useState(false);
  const [pickerPlacement, setPickerPlacement] = useState("bottom");
  const emojiTriggerRef = React.useRef(null);
  const PICKER_HEIGHT = 291;
  const QUICK_BAR_HEIGHT = 52;
  const VIEWPORT_MARGIN = 16;

  const computePlacement = (needsFullPickerHeight) => {
    const el = emojiTriggerRef.current;
    if (!el) return "bottom";

    const rect = el.getBoundingClientRect();
    const requiredSpace = (needsFullPickerHeight ? PICKER_HEIGHT : QUICK_BAR_HEIGHT) + VIEWPORT_MARGIN;

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow >= requiredSpace) return "bottom";
    if (spaceAbove >= requiredSpace) return "top";
    return spaceAbove > spaceBelow ? "top" : "bottom";
  };

  const isAlreadyReacted = (m, emoji) =>
    m.reactions && m.reactions[emoji] && m.reactions[emoji].includes(user.id);

  const location = useLocation();
  const screen =
    location.pathname.includes("schedule") && location.pathname.includes("task")
      ? "schedule"
      : location.pathname.includes("library")
        ? "library"
        : "messages";

  const { hasPermission: COMMENT_EDIT } = usePermission("15");
  const { hasPermission: LIB_COMMENT_EDIT } = usePermission("27");
  const commentsEditPermissions = screen === "schedule" ? COMMENT_EDIT : LIB_COMMENT_EDIT;
  const EDIT_WINDOW_MINUTES = 10;


  const isWithinEditWindow = (createdAt) => {
    if (!createdAt) return false;
    return dayjs().diff(dayjs(createdAt), "minute", true) <= EDIT_WINDOW_MINUTES;
  };
  const getMenuItems = (m, isOwn) => {
    const addReactionItem = {
      key: "add-reaction",
      icon: <EmojiIconV3 />,
      label: (
        <div
          className="text-gray-700 out-300-14"
          onClick={() => setOpenReactionFor(m._id)}
        >
          Add Reaction
        </div>
      ),
    };

    if (comments) {
      return [
        addReactionItem,
        commentsEditPermissions ? {
          key: "edit",
          icon: <EditIcon className="text-gray-700" />,
          label: <div className="text-gray-700 out-300-14" onClick={() => dispatch(updateConversations({ key: "editMessage", value: { edit: true, messageObject: m } }))}>Edit Comment</div>,
        } : null,
      ].filter(Boolean);
    }

    const isOwnForActions = (m.senderId ?? m.sender) == user.id;
    const isStarred =
      Array.isArray(m.starred) && m.starred.includes(String(user.id));
    const isRequestMsg = !!m.request?.type;
    const isRequestClosed = m.request?.status === "Closed";
    const canModify = isOwnForActions && isWithinEditWindow(m.createdAt);
    const canEdit = canModify && !m.forwarded;
    const chatKey = String(m.chat || m.threadId || currentThread.threadId);
    const isPinnedMsg = pinnedMessages?.[chatKey]?._id === m._id;
    const baseItems = [
      {
        key: "reply",
        icon: <MessageReplyIconV3 className="text-gray-700" />,
        label: <div className="text-gray-700 out-300-14" onClick={() => dispatch(updateConversations({ key: "replyMessage", value: { reply: true, message: m.message, messageObject: m } }))}>Reply</div>,
      },
      addReactionItem,
      {
        key: "forward",
        icon: <ForwardV3Icon className="text-gray-700" />,
        label: <div className="text-gray-700 out-300-14" onClick={() => dispatch(updateConversationsMultiple([{ key: "shareMessageModal", value: true }, { key: "forwardMessage", value: m }, { key: "shareType", value: "forwardMessage" }]))}>Forward</div>,
      },
      {
        key: "copy",
        icon: <CopyIcon className="text-gray-700" />,
        label: (
          <div
            className="text-gray-700 out-300-14"
            onClick={() => {
              navigator.clipboard?.writeText(m.message || m.content || "");
              dispatch(
                updateDashboard({
                  key: "alertPopupModal",
                  value: {
                    visible: true,
                    data: {
                      title: "Message copied",
                      subtitle: "Message copied successfully!",
                      description: "",
                    },
                  },
                })
              );
            }}
          >
            Copy
          </div>
        ),
      },
      {
        key: "star",
        icon: isStarred ? (
          <UnstarIcon className="text-gray-700" />
        ) : (
          <StarIcon className="text-gray-700" />
        ),
        label: (
          <div
            className="text-gray-700 out-300-14"
            onClick={() => onStarMessage?.(m, isStarred)}
          >
            {isStarred ? "Unstar" : "Star"}
          </div>
        ),
      },
      {
        key: "pin",
        icon: isPinnedMsg ? <UnPinIcon className="text-[#165ABF]" /> : <PinnedIcon className="text-primary-600" />,
        label: (
          <div
            className="text-gray-700 out-300-14"
            onClick={() => {
              if (isPinnedMsg) {
                onUnpinMessage?.(m);
              } else {
                setPinTargetMessage(m);
                setPinModalVisible(true);
              }
            }}
          >
            {isPinnedMsg ? "Un-Pin" : "Pin"}
          </div>
        ),
      },
    ];
    const ownExtras = [
      canEdit
        ? {
          key: "edit",
          icon: <EditV3Icon className="text-primary-600" />,
          label: (
            <div
              className="text-gray-700 out-300-14"
              onClick={() =>
                dispatch(updateConversations({ key: "editMessage", value: { edit: true, messageObject: m } }))
              }
            >
              Edit
            </div>
          ),
        }
        : null,
      {
        key: "info",
        icon: <InfoIcon className="text-primary-700" />,
        label: (
          <div
            className="text-gray-700 out-300-14"
            onClick={() =>
              dispatch(
                updateConversations({
                  key: "messageInfoDrawer",
                  value: { visible: true, message: m },
                })
              )
            }
          >
            Info
          </div>
        ),
      },
      isRequestMsg && !isRequestClosed
        ? {
          key: "close-request",
          icon: <RequestDocIcon className="text-gray-700" />,
          label: (
            <div className="text-gray-700 out-300-14" onClick={() => {/* TODO: wire up close request action */ }}>
              Close Request
            </div>
          ),
        }
        : null,
      canModify
        ? {
          key: "delete",
          icon: <DeleteV3Icon className="text-error-500" />,
          label: (
            <div
              className="text-gray-700 out-300-14"
              onClick={() => deleteMessage(m._id)}
            >
              Delete
            </div>
          ),
        }
        : null,
    ].filter(Boolean);

    const trailingItems = isOwnForActions
      ? ownExtras
      : isRequestMsg && !isRequestClosed
        ? [
          { key: "divider-request", type: "divider" },
          {
            key: "acknowledge-request",
            icon: <RequestDocIcon className="text-gray-700" />,
            label: (
              <div className="text-gray-700 out-300-14" onClick={() => {/* TODO: wire up acknowledge request action */ }}>
                Acknowledge Request
              </div>
            ),
          },
        ]
        : [];

    return [...baseItems, ...trailingItems];
  };

  const getAttachements = (attachments) => {
    if (!attachments.length) return null;
    return <Album files={attachments.map((a) => ({ ...a, messageId: message._id }))} message={message} />;
  };

  const messsageBody = (m) => {
    const senderId = m.senderId ?? m.sender;
    const isOwn = senderId == user.id;
    const senderProfilePicture = isOwn
      ? user.profilePicture
      : isDM
        ? currentThread.profilePicture
        : tenantMembers?.find((t) => t.id == senderId)?.profilePicture;

    const resolvedSenderMember = !isOwn
      ? tenantMembers?.find((t) => t.id == senderId)
      : null;
    const receivedByAll =
      Array.isArray(m.messageReceivedBy) &&
      m.messageReceivedBy.length > 0 &&
      participantsWithoutUser.length > 0 &&
      participantsWithoutUser.every((uid) =>
        m.messageReceivedBy.some((r) => String(r.userId) === String(uid))
      );

    const resolvedSenderName =
      m.senderName ||
      (resolvedSenderMember
        ? `${resolvedSenderMember.firstname || ""} ${resolvedSenderMember.lastname || ""}`.trim()
        : m.senderName);
    const readByAll =
      Array.isArray(m.messageReadBy) &&
      m.messageReadBy.length > 0 &&
      participantsWithoutUser.length > 0 &&
      participantsWithoutUser.every((uid) =>
        m.messageReadBy.some((r) => String(r.userId) === String(uid))
      );

    const PlusIcon = ({ className = "" }) => (
      <svg width="15" height="15" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
        <path d="M7.63672 14.6562H12.998V20.0176C12.998 20.5625 13.4463 21.0195 14 21.0195C14.5537 21.0195 15.002 20.5625 15.002 20.0176V14.6562H20.3633C20.9082 14.6562 21.3652 14.208 21.3652 13.6543C21.3652 13.1006 20.9082 12.6523 20.3633 12.6523H15.002V7.29102C15.002 6.74609 14.5537 6.28906 14 6.28906C13.4463 6.28906 12.998 6.74609 12.998 7.29102V12.6523H7.63672C7.0918 12.6523 6.63477 13.1006 6.63477 13.6543C6.63477 14.208 7.0918 14.6562 7.63672 14.6562Z" fill="#667085" />
      </svg>
    );

    const actionButtons = (
      <div
        className={`
      flex items-center gap-x-1
      opacity-0 scale-90 pointer-events-none
      group-hover:opacity-100 group-hover:scale-100 group-hover:pointer-events-auto
      transition-all duration-100 ease-out
      ${isOwn
            ? "absolute right-0 top-0 h-full origin-right"
            : "flex-shrink-0 ml-2 origin-left translate-x-2 group-hover:translate-x-0"
          }
    `}
      >
        {/* Emoji */}
        <Tooltip title="React" getPopupContainer={(t) => t.parentElement}>
          <Dropdown
            menu={{
              items: [{
                key: "r",
                label: showFullPicker ? (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="rounded-[12px] overflow-hidden"
                    style={{
                      "--epr-bg-color": "#F7FAFD",
                      "--epr-category-label-bg-color": "#F7FAFD",
                      "--epr-picker-border-color": "transparent",
                      "--epr-search-input-bg-color": "#FFFFFF",
                      "--epr-search-input-bg-color-active": "#FFFFFF",
                      "--epr-search-border-color": "#EAECF0",
                      "--epr-hover-bg-color": "#EAECF0",
                      "--epr-focus-bg-color": "#EAECF0",
                      "--epr-highlight-color": "#2E90FA",
                      "--epr-category-icon-active-color": "#2E90FA",
                      "--epr-text-color": "#344054",
                      "--epr-header-padding": "8px 10px",
                      "--epr-horizontal-padding": "10px",
                    }}
                  >
                    <Picker
                      emojiStyle={EmojiStyle.APPLE}
                      lazyLoadEmojis
                      width={292}
                      height={291}
                      previewConfig={{ showPreview: false }}
                      onEmojiClick={(emojiObject) => {
                        reactToMessage(emojiObject.emoji, m._id, isAlreadyReacted(m, emojiObject.emoji));
                        setOpenReactionFor(null);
                        setShowFullPicker(false);
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-x-2.5 py-3 px-3 w-full h-full">
                    {REACTIONS.map((reaction) => (
                      <button
                        key={reaction.reaction}
                        className="w-[26px] h-[26px] py-px flex-shrink-0 flex items-center justify-center hover:scale-125 transition-transform"
                        onClick={(e) => {
                          e.stopPropagation();
                          reactToMessage(reaction.emoji, m._id, isAlreadyReacted(m, reaction.emoji));
                          setOpenReactionFor(null);
                        }}
                      >
                        <AppleEmoji emoji={reaction.emoji} size={24} />
                      </button>
                    ))}
                    <button
                      className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPickerPlacement(computePlacement(true));
                        setShowFullPicker(true);
                      }}
                    >
                      <PlusIcon className="w-8 h-8" />
                    </button>
                  </div>
                )
              }]
            }}
            trigger={["click"]}
            open={openReactionFor === m._id}
            onOpenChange={(visible) => {
              if (visible) setPickerPlacement(computePlacement(false));
              setOpenReactionFor(visible ? m._id : null);
              if (!visible) setShowFullPicker(false);
            }}
            placement={
              pickerPlacement === "top"
                ? (isOwn ? "topRight" : "topLeft")
                : (isOwn ? "bottomRight" : "bottomLeft")
            }
            dropdownRender={(menu) => (
              <div
                className="w-fit h-[52px] flex items-center bg-[#F9FBFE] rounded-[12px]
        shadow-[0px_12px_16px_-4px_rgba(16,24,40,0.08),0px_4px_6px_-2px_rgba(16,24,40,0.03)]
        [&_.ant-dropdown-menu]:!p-0 [&_.ant-dropdown-menu]:!bg-transparent [&_.ant-dropdown-menu]:!shadow-none [&_.ant-dropdown-menu]:!rounded-[12px]
        [&_.ant-dropdown-menu-item]:!p-0 [&_.ant-dropdown-menu-item]:!m-0
        [&_.ant-dropdown-menu-item]:!bg-transparent
        [&_.ant-dropdown-menu-item-active]:!bg-transparent
        [&_.ant-dropdown-menu-item:hover]:!bg-transparent"
              >
                {menu}
              </div>
            )}
          >
            <button
              ref={emojiTriggerRef}
              className={`w-5 h-5 flex items-center justify-center rounded transition-all duration-100 ease-out hover:scale-110
${isOwn ? "hover:bg-black/20" : "text-primary-600 hover:bg-primary-50"}`}
            >
              {isOwn ? (
                <EmojiWhiteIcon className="w-[18px] h-[18px]" />
              ) : (
                <EmojiIconV3 className="w-[14px] h-[13px]" />
              )}
            </button>
          </Dropdown>
        </Tooltip>

        {/* Reply */}
        <Tooltip title="Reply" getPopupContainer={(t) => t.parentElement}>
          <button
            className={`w-5 h-5 flex items-center justify-center rounded transition-all duration-100 ease-out hover:scale-110
          ${isOwn
                ? "[&_svg]:!fill-white [&_svg_path]:!fill-white [&_svg_path]:!stroke-white hover:bg-black/20"
                : "text-primary-600 hover:bg-primary-50"
              }`}
            onClick={() => dispatch(updateConversations({ key: "replyMessage", value: { reply: true, message: m.message, messageObject: m } }))}
          >
            <MessageReplyIconV3 className="w-[14px] h-[13px]" />
          </button>
        </Tooltip>

        {/* Three-dot more */}
        <Tooltip title="More" getPopupContainer={(t) => t.parentElement}>
          <Dropdown
            menu={{ items: getMenuItems(m, isOwn) }}
            trigger={["click"]}
            placement={isOwn ? "bottomRight" : "bottomLeft"}
            dropdownRender={(menu) => <div className="w-[220px]">{menu}</div>}
          >
            <button
              className={`w-5 h-5 flex items-center justify-center rounded transition-all duration-100 ease-out hover:scale-110
            ${isOwn
                  ? "text-white hover:bg-black/20"
                  : "text-primary-600 hover:bg-primary-50"
                }`}
            >
              <DotsVerticalIcon className="w-[14px] h-[13px]" />
            </button>
          </Dropdown>
        </Tooltip>
      </div>
    );

    // ── Reply quote content ──────────────────────────────────────────────

    const replyAttachment = m.replyMessage?.attachments?.[0];
    const replyFileTag = replyAttachment ? getReplyFileTag(replyAttachment.type) : null;

    return (
      <div className={`flex ${isOwn ? "justify-end" : "justify-start"} px-4 py-0.5`}>
        <div className={`flex flex-col ${isOwn ? "min-w-[216px] min-h-[64px] max-w-[560px]" : "min-w-[216px] min-h-[64px] max-w-[488px]"} group ${isOwn ? "items-end" : "items-start"}`}>

          {/* ── Bubble ────────────────────────────────────────────────── */}
          <div
            className={`
relative w-full min-w-[216px] min-h-[64px] px-3 pt-[10px] pb-2 transition-all duration-100 ease-out border
${isOwn
                ? "bg-primary-600 border-primary-600 text-white rounded-[12px]  group-hover:bg-[#194185] group-hover:border-[#0F2B5C]"
                : "bg-white border-gray-200 text-gray-900 rounded-[12px]  group-hover:bg-[#EFF8FF] group-hover:border-[#D1E9FF]"
              }
`}
          >
            <div className="flex items-center gap-x-1.5 mb-[8px] min-w-0 relative">
              {!isOwn && (
                <CustomAvatar
                  title={resolvedSenderName}
                  size={22}
                  src={senderProfilePicture}
                  whiteText
                  userId={m.senderId}
                  color={isDM ? currentThread.profilePictureColorCode : undefined}
                />
              )}
              <div className={`min-w-0 flex-shrink overflow-hidden flex items-center gap-x-1.5 ${isOwn ? "[&_*]:!text-white" : "[&_*]:!text-[#292927]"}`}>
                <UserDetailsCard
                  userName={isOwn ? "You" : resolvedSenderName}
                  isDisable={false}
                  userId={m.senderId}
                  className="truncate min-w-0 max-w-[140px] flex-shrink"
                />
                <Tooltip title={m.createdAt ? dayjs(m.createdAt).utc().local().format("MMMM DD YYYY, hh:mm a") : "-"}>
                  <span className={`out-300-10 flex-shrink-0 whitespace-nowrap ${isOwn ? "text-white/60" : "text-gray-400"}`}>
                    {m.createdAt ? dayjs(m.createdAt).utc().local().format("hh:mm a") : "-"}
                  </span>
                </Tooltip>

              </div>
              {m.forwarded && (
                <span className={`out-300-12 flex items-center gap-x-1 flex-shrink-0 whitespace-nowrap`}
                  style={{ color: isOwn ? "#DDE5ED" : "#98A2B3" }}
                >
                  <ForwardV3MonoIcon className="w-3 h-3 flex-shrink-0" />
                  Forwarded
                </span>
              )}
              {m.edited && (
                <span
                  className="out-300-12 flex-shrink-0 whitespace-nowrap"
                  style={{ color: isOwn ? "#DDE5ED" : "#98A2B3" }}
                >
                  Edited
                </span>
              )}
              {/* Star, moved here so it sits next to the timestamp for both own & other bubbles */}
              <span
                className={`flex-shrink-0 w-[15px] h-[15px] ${Array.isArray(m.starred) && m.starred.includes(String(user.id)) ? "" : "invisible"
                  }`}
              >
                <StarIcon
                  className={isOwn ? "text-yellow-300" : "text-yellow-500"}
                  filled
                  width={15}
                  height={15}
                />
              </span>
              {m.request?.type && (
                <img src="/images/v2/conversations/AttachmentHeader.svg" className="w-[26px] h-5 flex-shrink-0" />
              )}
              <div className="flex-1" />
              {isOwn && (
                <div className="flex-shrink-0 transition-transform duration-200 ease-out group-hover:-translate-x-[72px]">
                  {receivedByAll || readByAll ? (
                    <DoubleTickIcon
                      className="flex-shrink-0"
                      style={{ color: readByAll ? "#FEB273" : "#F9FBFE" }}
                    />
                  ) : (
                    <SingleTickIcon
                      className="flex-shrink-0"
                      style={{ color: "#F9FBFE" }} />

                  )}
                </div>
              )}

              {actionButtons}
            </div>


            {m.replyMessage && m.replyMessage._id && (
              <div
                className={`relative mb-2 rounded-[8px] p-2 max-w-[472px] border ${isOwn ? "bg-primary-700 border-[#194185]" : "bg-gray-50 border-gray-200"
                  }`}
              >
                <div className="flex items-start gap-1.5">
                  <div className="min-w-0 flex-1 flex flex-col gap-1">
                    <div className={`capitalize truncate ${isOwn ? "out-500-12 text-gray-200" : "out-600-14 text-gray-700"}`}>{m.replyMessage.senderName}</div>
                    <div className={`flex items-center gap-x-1 min-w-0 ${isOwn ? "[&_*]:!text-gray-200" : ""}`}>
                      {replyAttachment && (
                        <div className="flex items-center gap-x-1 flex-shrink-0">
                          <img src={replyFileTag.url} className="w-3.5 h-3.5" />
                          <span className={`out-300-12 whitespace-nowrap ${isOwn ? "text-gray-200" : "text-gray-500"}`}>
                            {replyFileTag.type}
                          </span>
                        </div>
                      )}
                      <ParseMentions value={m.replyMessage.message || m.replyMessage.content} className={`out-300-12 truncate ${isOwn ? "text-gray-200" : "text-gray-500"}`} />
                    </div>
                  </div>
                  {replyAttachment && (
                    <div
                      className="w-[50px] h-[50px] rounded-[4px] bg-no-repeat bg-center bg-cover flex-shrink-0"
                      style={{
                        backgroundImage: `url(${replyAttachment.thumbnail || replyAttachment.url})`,
                      }}
                    />
                  )}
                </div>
                {/* Left accent bar — Figma: 2px wide, hug height, 2px radius, Primary/500 #2E90FA.
                    Width is forced via inline style rather than the w-[2px]
                    arbitrary-value class, which was rendering noticeably
                    thicker — likely not being picked up by the Tailwind build. */}
                <div
                  className="absolute left-0 top-2 bottom-2 bg-primary-500 rounded-[2px]"
                  style={{ width: "2px" }}
                />
              </div>
            )}

            {/* ── Message text ── */}
            <div className={isOwn ? "[&_*]:!text-white" : "[&_*]:!text-gray-700"}>
              <ParseMentions value={m.message || m.content} className={isOwn ? "text-white" : "text-gray-900"} />
            </div>

            {/* ── Attachments ── */}
            {m.attachments && m.attachments.length > 0 && (
              <div className="mt-1">{getAttachements(m.attachments)}</div>
            )}

            {m.reactions && Object.keys(m.reactions).some((e) => m.reactions[e]?.length > 0) && (
              <div className="flex items-center flex-wrap gap-x-1.5 gap-y-1 mt-2 justify-start">
                {Object.keys(m.reactions).map((emoji) => {
                  const userIds = m.reactions[emoji];
                  if (!userIds?.length) return null;
                  const isMine = userIds.includes(user.id);
                  const showHighlight = !isOwn && isMine;
                  return (
                    <Tooltip key={emoji} title={userIds.map((id) => { const u = tenantMembers?.find((mb) => mb.id === id); return u ? `${u.firstname} ${u.lastname}` : null; }).filter(Boolean).join(", ")}>
                      <button className="flex items-center" onClick={() => reactToMessage(emoji, m._id, isMine)}>
                        <div
                          className={`flex items-center justify-center max-w-[30px] h-6 rounded-2xl border px-2 gap-1 shadow-xs ${showHighlight ? "bg-primary-50 border-primary-500" : "bg-white border-gray-200"
                            }`}
                        >
                          <AppleEmoji emoji={emoji} size={14} />
                          {userIds.length > 1 && <span className="out-500-12 text-gray-700">{userIds.length}</span>}
                        </div>
                      </button>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const isOwnMessage = (message.senderId ?? message.sender) == user.id;

  const [, forceExpireTick] = useState(0);
  useEffect(() => {
    if (!isOwnMessage || !message.createdAt) return;
    const msUntilExpiry = dayjs(message.createdAt)
      .add(EDIT_WINDOW_MINUTES, "minute")
      .diff(dayjs());
    if (msUntilExpiry <= 0) return;
    const timer = setTimeout(() => forceExpireTick((n) => n + 1), msUntilExpiry + 250);
    return () => clearTimeout(timer);
  }, [isOwnMessage, message.createdAt]);
  return (
    <>
      {message.activity ? (
        <Activity
          activity={message.activity}
          message={message}
          user={user}
          threadInfo={currentThread}
        />
      ) : (
        <div className="scroll-mt-[20vh] relative" data-message-id={message._id}>
          {messsageBody(message)}
          {message.request?.activity?.length > 0 && (
            <div className="pl-6 my-5 ml-10 border-l">
              <div className="flex cursor-pointer gap-x-1 items-center" onClick={() => setRepliesOpen(!repliesOpen)}>
                <p className="text-primary-600 out-500-14">{message.request?.activity?.length} Reply</p>
                <ChevronIcon className="text-gray-700" />
              </div>
              {repliesOpen && (
                <div className="mt-3 space-y-2">
                  {message.request?.activity.map((m) => <div key={m._id}>{messsageBody(m)}</div>)}
                  {message.request?.status === "Closed" && (
                    <div className="mt-1 text-success-600 out-500-14">
                      Closed this request on{" "}
                      {message.request?.requestClosingDate
                        ? dayjs(message.request?.requestClosingDate).utc().local().format("MMMM DD YYYY, hh:mm a")
                        : "-"}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

        </div>
      )}
      <PinDurationModal
        visible={pinModalVisible}
        onClose={() => {
          setPinModalVisible(false);
          setPinTargetMessage(null);
        }}
        onConfirm={(duration) => {
          if (pinTargetMessage) onPinMessage?.(pinTargetMessage, duration);
          setPinModalVisible(false);
          setPinTargetMessage(null);
        }}
      />
    </>
  );
};

export const Day = ({ date }) => {
  const formateDate = (date) => {
    if (date === null || typeof date === "undefined") return "-";
    if (dayjs(date).isToday()) return "Today";
    if (dayjs(date).isTomorrow()) return "Tomorrow";
    return dayjs(date).format("ddd, D MMM");
  };
  return (
    <div className="px-[10px] py-1 mx-auto my-[14px] border border-[#DDE5ED] w-max rounded-[12px] bg-[#F9FBFE]">
      <p className="text-[#344054] text-xs leading-[18px] font-medium">{formateDate(date)}</p>
    </div>
  );
};

export const Unread = ({ count }) => (
  <div className="w-full my-[14px] text-center underline out-500-14 text-error-500">
    {count} New messages
  </div>
);

export function TypingIndicator({ typingStatus, currentThread }) {
  const isPrivateThread = currentThread.type === "private";
  const isMatchingUser = +typingStatus.userId === +currentThread.userId;
  const isMatchingThread = +typingStatus.threadId === +currentThread.threadId;

  if (!typingStatus.isTyping || (isPrivateThread ? !isMatchingUser : !isMatchingThread)) return <></>;
  if (currentThread.type !== typingStatus.threadType) return <></>;

  return (
    <div className="pl-16 mt-1.5 mb-0 flex items-center">
      <div className="dot-collision" />
      <span className="italic text-gray-500 ml-7 mon-500-10">
        {currentThread.type === "group" ? typingStatus.userName + " is" : ""} Typing.....
      </span>
    </div>
  );
}

export default Message;